import React from 'react'

import { StyledRemoteString } from '@acorns/web-utils'

import { FlowRoute } from 'utils/FlowProvider/types'

export const TIERED_HERO_COPY = new Map([
  [
    FlowRoute.Signup,
    {
      header: 'Create Account',
      text: (
        <>
          <strong style={{ whiteSpace: 'pre-wrap' }}>{'Welcome to Acorns. Grow your oak!'}</strong>
        </>
      ),
      veteranText: (
        <>
          <strong>Welcome To Acorns!</strong>
        </>
      ),
      subtext: (
        <>
          <p>
            Easily save & invest in the background of life. Invest your spare change, save for
            retirement, spend smarter, earn more, and grow your knowledge.
          </p>
        </>
      ),
      veteranSubtext: (
        <>
          <p>
            Sign up now to save & invest every day with Acorns. Plus, we'll invest $30 in you after
            you make your first investment this month. Proof of service will be required.
          </p>
        </>
      ),
      footnote: 'Protected by bank-level security',
      veteranCaption:
        'Gift and waiver provided to first 5,000 people that meet these criteria: ' +
        'New Acorns customers. Must be verified, link a bank account, and make an initial investment. ' +
        'Acorns reserves the right to check military eligibility.',
      footnoteLock: true,
    },
  ],
  [
    FlowRoute.Funding,
    {
      title: 'Link your bank',
      header: 'Link your bank',
      text:
        'Link your bank account to make your first investment. Select Automatic below to enable investing your spare change with Round-Ups®!',
      footnote: 'Your data is protected with 256-bit encryption and is never stored on your phone.',
    },
  ],
  [
    FlowRoute.Institutions,
    {
      header: 'Link your bank',
      text: 'Protected by bank-level security and 256-bit encryption.',
    },
  ],
  [
    FlowRoute.ManualFundingSource,
    {
      header: 'Link your bank account',
      text:
        'We need a little more information to link your bank account, so you can fund your investments.',
      footnote: 'We use bank-level security, data encryption, and more.',
      mobileFootnote:
        'Your data is protected with 256-bit encryption and is never stored on your phone.',
      footnoteLock: true,
    },
  ],
  [
    FlowRoute.MOCInterstitial,
    {
      header: 'Mighty Oak Card',
    },
  ],
  [
    FlowRoute.PersonalInfo,
    {
      header: 'Tell us a little about you',
      text: <>This information helps us personalize and secure your Acorns account.</>,
      footnote: 'Your data is protected with 256-bit encryption',
      footnoteLock: true,
    },
  ],
  [
    FlowRoute.SSN,
    {
      header: 'Verify your Identity',
      text: (
        <>
          <strong>Verify your identity</strong>
        </>
      ),
      subtext: (
        <p>
          We’re legally required to ask for your SSN to open your account. We encrypt your data to
          keep your info safe.
        </p>
      ),
      footnote: 'Your info is encrypted.',
      footnoteLock: true,
    },
  ],
  [
    FlowRoute.SuitabilityQuestions,
    {
      header: 'Your finances and goals',
      text: (
        <>
          <strong>Your finances and goals</strong>
        </>
      ),
      subtext: (
        <StyledRemoteString
          stringKey="invest.onboarding.suitability.subtitle.v1.markdown"
          screen="registrationPortfolioQuestions"
        />
      ),
    },
  ],
  [
    FlowRoute.ContactInfo,
    {
      header: 'Contact Information',
      text: <>Acorns is required to collect this information to open your account.</>,
      footnote: 'standard rates may apply',
      footnoteLock: true,
      footerText: 'Standard message rates may apply.',
    },
  ],
  [
    FlowRoute.MFASetup,
    {
      header: 'Verify Phone Number',
    },
  ],
  [
    FlowRoute.InvestInterstitial,
    {
      header: 'Invest account',
      text: (
        <StyledRemoteString
          stringKey="registration.invest_interstitial.title.v1.markdown"
          screen="registrationInvestInterstitial"
        />
      ),
      disclaimer:
        'We use bank-level security, the highest levels of data encryption, and your information is never stored on your device.',
      ctaLabel: 'Next',
      footnote:
        'We use bank-level security, the highest levels of data encryption, and your information is never stored on your device.',
    },
  ],
  [
    FlowRoute.InvestorQuestions,
    {
      header: 'More About You',
      text: (
        <>
          <strong>These don’t apply to most people, but if they apply to you, let us know</strong>
        </>
      ),
    },
  ],
  [
    FlowRoute.PricingInterstitial,
    {
      header: '',
    },
  ],
  [
    FlowRoute.OneTimeInvestment,
    {
      header: 'One-time Investment',
      text: 'Pick an amount to jumpstart your account today.',
    },
  ],
  [
    FlowRoute.RecurringInvestment,
    {
      header: 'Recurring Investment',
      text:
        'Set it, forget it, and give your money a chance to grow before you can spend it. You can change this any time.',
    },
  ],
  [
    FlowRoute.BankLinkSuccess,
    {
      header: 'Success',
      title: 'Successfully linked',
      text:
        'Every time you add money to Acorns, it will transfer from your primary checking account.',
    },
  ],
  [
    FlowRoute.ChooseFundingSource,
    {
      header: 'Success',
    },
  ],
  [
    FlowRoute.RoundUpsInterstitial,
    {
      header: 'Round-Ups®',
      secondaryCtaLabel: 'Important Round-Ups® Information',
      textRoundUpsAreOn:
        "Every time you buy something, like gas or coffee, we'll round the purchase up to the next dollar and invest it for you. Just link your debit or credit cars and we'll take it from there.",
      ctaLabelRoundUpsAreOn: 'Continue',
      disclaimerTextRoundUpsAreOn: 'You can turn Round-Ups® off in settings any time',
    },
  ],
  [
    FlowRoute.RoundUpsSetup,
    {
      header: 'Round-Ups®',
      text: 'Do you want to invest your Round-Ups® automatically or manually?',
      ctaLabel: 'Next',
    },
  ],
  [
    FlowRoute.RoundUpsFunding,
    {
      header: 'Round-Ups®',
      text:
        'Choose which accounts you want us to use to Round-up your purchases and invest the change',
      linkedAccountsText:
        'Invested from your primary checking account after you have $5 worth of change.',
      ctaLabel: 'Next',
    },
  ],
  [
    FlowRoute.RoundUpsPast,
    {
      header: 'We Found Past Round-Ups®',
      text: 'Do you want to invest Rounds-Ups® from some past purchases with your linked accounts?',
      mobileText:
        'Do you want to invest Rounds-Ups® from some past purchases with your linked accounts?',
      ctaLabel: 'Round-Up and Invest $',
    },
  ],
  [
    FlowRoute.PreviewPortfolio,
    {
      header: 'Recommended Portfolio',
    },
  ],
  [
    FlowRoute.CryptoLander,
    {
      header: 'Add Bitcoin ETF',
    },
  ],
  [
    FlowRoute.AboutPortfolio,
    {
      header: 'About Portfolio',
    },
  ],
  [
    FlowRoute.SelectTier,
    {
      header: 'Choose your subscription',
    },
  ],
  [
    FlowRoute.SecondChanceBankLink,
    {
      header: 'Ready to Invest',
    },
  ],
  [
    FlowRoute.DirectToMobileLander,
    {
      header: 'Download',
    },
  ],
  [
    FlowRoute.Success,
    {
      header: 'Success',
    },
  ],
  [
    FlowRoute.ReferralReward,
    {
      header: 'Your Reward',
    },
  ],
  [
    FlowRoute.ReferralDisclosure,
    {
      header: 'Referral Disclosure',
    },
  ],
])
